import { useZero } from "@rocicorp/zero/react";
import { classifyZeroResult } from "../lib/zeroResultError";

export function mutationErrorMessage(
  error: unknown,
  fallback = "Failed to connect connector"
): string {
  const decision = classifyZeroResult({ type: "error", error });
  if (decision.kind === "toast" && decision.message) return decision.message;
  return fallback;
}
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getInitialConnectorValues } from "../../shared/connectors/getInitialConnectorValues";
import { isDraft } from "../../shared/connectors/isDraft";
import { matchAvailableApps } from "../../shared/connectors/matchAvailableApps";
import type { ConnectorDefinition } from "../../shared/connectors/types";
import { isTriviallyMatchingPattern } from "../../shared/connectors/urlPatternConversion";
import { urlToPattern } from "../../shared/connectors/urlToPattern";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import {
  isSupplierAvailableFromGrants,
  principalHasSupplierPermissionFromGrants,
} from "../../shared/rbac/crossResourceAvailability";
import { Button } from "../components/Button";
import { ConnectorLogo } from "../components/ConnectorLogo";
import { ConnectorRow } from "../components/ConnectorRow";
import { useRenderWithPlaceholders } from "../components/ConnectorModal/renderWithPlaceholders";
import { FooterAction } from "../components/FooterAction";
import { Icon } from "../components/Icon";
import { Text } from "../components/Text";
import { useCurrentUserGroups } from "../hooks/useCurrentUserGroups";
import { useDefaultConnectorGrants } from "../hooks/useDefaultConnectorGrants";
import { useGroupMemberships } from "../hooks/useGroupMemberships";
import {
  type SelectableConnector,
  useConnectorSelectOnSave,
} from "../hooks/useConnectorSelectOnSave";
import { useHasAccess } from "../hooks/useHasAccess";
import { useQuery } from "../hooks/useQuery";
import {
  connectorDirectory,
  recommendedCustomConnectors,
} from "../lib/connectorDirectory";
import {
  canCreateConnectorInChat,
  connectPromptState,
  creatableConnectorIdentifiers,
} from "../lib/connectPrompt";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import { isEditableTarget } from "../lib/isEditableTarget";

const PromptContainer = styled("div")`
  border-radius: 12px;
  outline: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  padding: 4px;
`;

const PromptHeader = styled("div")`
  display: flex;
  padding: 10px 8px;
  box-sizing: content-box;
  align-items: center;
  color: var(--text-secondary);
`;

const PromptBody = styled("div")`
  display: flex;
  flex-direction: column;
  padding: 4px 0;
  gap: 4px;
  max-height: 200px;
  overflow-y: auto;
  @media (max-width: 768px) {
    max-height: 40dvh;
  }
`;

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  padding-right: 28px;
  cursor: pointer;
  border-radius: 8px;
  position: relative;
  &[data-highlighted="true"] {
    background: var(--alpha4);
  }
`;

const Footer = styled("div")`
  display: flex;
  margin: 0 -4px;
  padding: 12px 12px 8px 12px;
  border-top: 0.5px solid var(--border-alpha);
  justify-content: space-between;
`;

const Keyboard = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: 2px;
  background: var(--alpha4);
  color: var(--text-secondary);
`;

const Instructions = styled("div")`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  gap: 4px;
`;

function urlConfigField(definition: ConnectorDefinition): string | undefined {
  for (const step of definition.steps) {
    for (const item of step.items) {
      if (item.kind === "url" && item.target.kind === "configuration") {
        return item.target.field;
      }
    }
  }
  return undefined;
}

const UnavailableMessage = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 8px 8px;
`;

const UnavailableRow = styled("div")`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

type Pending = {
  toolCallId: string;
  step?: string;
  targetUrl?: string;
  search?: string;
  // Narrows the prompt to one specific connector the agent asked to use in
  // chat — the only offer is picking it, never creating a new one.
  connectorId?: string;
  appIdentifiers: string[];
};

type Props = {
  pending: Pending;
  openCreatedApp: (connectedAppId: string) => void;
  onDismiss: () => void;
  onTypeInstead: () => void;
  onNoApps?: () => void;
  onError?: (message: string) => void;
  onSelectApp: (app: SelectableConnector) => void | Promise<void>;
  spaceId?: string;
  othersPresent?: boolean;
};

export function ConnectorPrompt({
  pending,
  openCreatedApp,
  onDismiss,
  onTypeInstead,
  onNoApps,
  onError,
  onSelectApp,
  spaceId,
  othersPresent,
}: Props) {
  const z = useZero();
  const renderWithPlaceholders = useRenderWithPlaceholders();
  const canCreateApp = useHasAccess("tenant:connected_app_create");
  const canShare = useHasAccess("tenant:connected_app_share");
  const [space] = useQuery(spaceId ? queries.navSpace({ spaceId }) : undefined);
  const { appIdentifiers = [], targetUrl, search, connectorId } = pending;
  const canCreateHere =
    !connectorId &&
    canCreateConnectorInChat({
      canCreateApp,
      canShare,
      spaceId,
      spaceMode: space?.mode,
    });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);

  const creatableIdentifiers = useMemo(
    () => creatableConnectorIdentifiers(canCreateHere, appIdentifiers),
    [canCreateHere, appIdentifiers]
  );

  const [allAppsRaw, allAppsDetails] = useQuery(queries.connectedAppsIndex());
  const [spaceGrants] = useQuery(
    spaceId ? queries.roleGrantsByScope({ scopeId: spaceId }) : undefined
  );
  const defaultConnectorGrants = useDefaultConnectorGrants(spaceId);
  const { currentUser, groupIds: currentUserGroupIds } = useCurrentUserGroups();
  const { userGroupIds: allUserGroupMemberships } = useGroupMemberships();
  const allApps = useMemo(() => {
    if (spaceId && spaceGrants) {
      return allAppsRaw.filter((app) =>
        isSupplierAvailableFromGrants(
          "space_uses_connected_apps",
          spaceGrants,
          app.roleGrantsAsScope,
          allUserGroupMemberships
        )
      );
    }
    return allAppsRaw.filter((app) =>
      principalHasSupplierPermissionFromGrants(
        currentUser?.id ?? "",
        currentUserGroupIds,
        app.roleGrantsAsScope,
        "connected_app:use"
      )
    );
  }, [
    allAppsRaw,
    spaceId,
    spaceGrants,
    currentUser,
    currentUserGroupIds,
    allUserGroupMemberships,
  ]);
  const existingApps = useMemo(() => {
    const candidates = allApps.filter((app) => !isDraft(app));
    if (connectorId) return candidates.filter((app) => app.id === connectorId);
    return matchAvailableApps(candidates, { targetUrl, search }).matched;
  }, [allApps, connectorId, targetUrl, search]);

  const handleSelectApp = useCallback(
    async (app: SelectableConnector) => {
      await onSelectApp(app);
    },
    [onSelectApp]
  );

  const trackCreatedApp = useConnectorSelectOnSave((connector) => {
    void handleSelectApp(connector);
  });

  const handleCreateNew = useCallback(
    (identifier: string) => {
      const appDef = connectorDirectory.find(
        (app) => app.identifier === identifier
      );
      if (!appDef) return;

      const initialValues = getInitialConnectorValues(
        appDef.definition,
        renderWithPlaceholders
      );

      const configuration = { ...initialValues.configuration };
      let urlPattern = initialValues.urlPattern;
      let fallbackName = targetUrl ?? search ?? "";

      if (targetUrl) {
        let origin = targetUrl;
        try {
          const parsed = new URL(targetUrl);
          fallbackName = parsed.hostname;
          origin = parsed.origin;
        } catch {}

        const urlField = urlConfigField(appDef.definition);
        if (urlField && !configuration[urlField]) {
          configuration[urlField] = targetUrl;
        }

        if (!urlPattern) {
          const derived = appDef.definition.defaultUrlPattern(configuration);
          urlPattern =
            derived && !isTriviallyMatchingPattern(derived)
              ? derived
              : urlToPattern(origin);
        }
      }

      const newAppId = nanoid(12);
      if (!defaultConnectorGrants) return;

      const result = z.mutate(
        sharedMutators.createConnectedApp({
          connectedAppId: newAppId,
          name: initialValues.name || fallbackName,
          identifier,
          urlPattern,
          configuration,
          secrets: {},
          createOAuthConnection: initialValues.createOAuthConnection,
          grants: defaultConnectorGrants,
        })
      );
      trackCreatedApp(newAppId);
      openCreatedApp(newAppId);
      void result.server.then((serverResult) => {
        if (serverResult.type === "error") {
          onError?.(
            mutationErrorMessage(
              serverResult.error,
              "Failed to create connector"
            )
          );
        }
      });
    },
    [
      openCreatedApp,
      defaultConnectorGrants,
      onError,
      renderWithPlaceholders,
      search,
      targetUrl,
      trackCreatedApp,
      z,
    ]
  );

  const showCustomConnectors =
    canCreateHere && creatableIdentifiers.length === 0;

  const handleConfirmSelected = useCallback(() => {
    if (selectedIndex < existingApps.length) {
      const app = existingApps[selectedIndex];
      if (app) void handleSelectApp(app);
      return;
    }

    const createIndex = selectedIndex - existingApps.length;
    if (createIndex < creatableIdentifiers.length) {
      const identifier = creatableIdentifiers[createIndex];
      if (identifier) handleCreateNew(identifier);
      return;
    }

    if (showCustomConnectors) {
      const connector =
        recommendedCustomConnectors[createIndex - creatableIdentifiers.length];
      if (connector) handleCreateNew(connector.identifier);
    }
  }, [
    creatableIdentifiers,
    existingApps,
    handleCreateNew,
    handleSelectApp,
    selectedIndex,
    showCustomConnectors,
  ]);

  const itemCount =
    existingApps.length +
    creatableIdentifiers.length +
    (showCustomConnectors ? recommendedCustomConnectors.length : 0);
  const state = connectPromptState({
    itemCount,
    singleViewerPath: !!onNoApps,
  });

  const appsQueryReady = allAppsDetails?.type === "complete";
  const firedNoApps = useRef(false);
  useEffect(() => {
    if (
      appsQueryReady &&
      state === "resolveEmpty" &&
      !firedNoApps.current &&
      onNoApps
    ) {
      firedNoApps.current = true;
      onNoApps();
    }
  }, [appsQueryReady, state, onNoApps]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEditableTarget(e)) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((index) => Math.min(index + 1, itemCount - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((index) => Math.max(index - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        handleConfirmSelected();
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) onTypeInstead();
        else onDismiss();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleConfirmSelected, itemCount, onDismiss, onTypeInstead]);

  useEffect(() => {
    bodyRef.current
      ?.querySelector('[data-highlighted="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  // resolveEmpty: single viewer with nothing to offer — onNoApps auto-resolves,
  // so render nothing. unavailable: shared workflow path where this viewer can't
  // act; show why and let them dismiss rather than silently resolving the prompt
  // for every collaborator or blanking the composer.
  if (state === "resolveEmpty") return null;
  if (state === "unavailable") {
    return (
      <UnavailablePrompt
        target={targetUrl ?? search}
        othersPresent={othersPresent ?? false}
        onDismiss={onDismiss}
        onTypeInstead={onTypeInstead}
      />
    );
  }

  return (
    <PromptContainer>
      <PromptBody ref={bodyRef}>
        <PromptHeader>
          <Text variant="text-small">
            Connect to {targetUrl ?? search ?? existingApps[0]?.name}
          </Text>
        </PromptHeader>
        {existingApps.map((app, index) => {
          return (
            <Row
              key={app.id}
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={() => handleSelectApp(app)}
              data-highlighted={index === selectedIndex}
            >
              <ConnectorRow
                app={app}
                size={16}
                name={app.name}
                secondary={
                  app.createdBy
                    ? `Added by ${app.createdBy.name || app.createdBy.email} ${formatRelativeTime(app.createdAt)}`
                    : `Added ${formatRelativeTime(app.createdAt)}`
                }
              />
              {index === selectedIndex && (
                <Keyboard
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <Icon name="corner-down-left" size={12} />
                </Keyboard>
              )}
            </Row>
          );
        })}
        {creatableIdentifiers.map((identifier, offset) => {
          const index = existingApps.length + offset;
          const appDef = connectorDirectory.find(
            (app) => app.identifier === identifier
          );
          const hasExisting = existingApps.length > 0;
          return (
            <Row
              key={identifier}
              onClick={() => handleCreateNew(identifier)}
              onMouseEnter={() => setSelectedIndex(index)}
              data-highlighted={selectedIndex === index}
            >
              {hasExisting ? (
                <Icon name="plus" size={16} />
              ) : (
                <ConnectorLogo template={{ name: "", identifier }} size={16} />
              )}
              <Text variant="text-regular" color="var(--text-primary)">
                {hasExisting
                  ? `Create a new ${appDef?.definition.name ?? identifier} connection`
                  : `Connect with ${appDef?.definition.name ?? identifier}`}
              </Text>
              {selectedIndex === index && (
                <Keyboard
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <Icon name="corner-down-left" size={12} />
                </Keyboard>
              )}
            </Row>
          );
        })}
        {showCustomConnectors &&
          recommendedCustomConnectors.map((connector, offset) => {
            const index =
              existingApps.length + creatableIdentifiers.length + offset;
            return (
              <Row
                key={connector.identifier}
                onClick={() => handleCreateNew(connector.identifier)}
                onMouseEnter={() => setSelectedIndex(index)}
                data-highlighted={selectedIndex === index}
              >
                <ConnectorRow
                  template={{
                    name: connector.name,
                    identifier: connector.identifier,
                  }}
                  size={16}
                  name={
                    <>
                      {connector.name}{" "}
                      <Text variant="text-small" color="var(--text-secondary)">
                        {connector.tagLine}
                      </Text>
                    </>
                  }
                />
                {selectedIndex === index && (
                  <Keyboard
                    style={{
                      position: "absolute",
                      right: 8,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  >
                    <Icon name="corner-down-left" size={12} />
                  </Keyboard>
                )}
              </Row>
            );
          })}
      </PromptBody>
      <Footer>
        <Instructions>
          <FooterAction onPress={onDismiss}>
            <Text variant="text-smaller-md">Skip</Text>
            <Keyboard style={{ padding: "2px 4px" }}>
              <Text variant="text-smaller-md">esc</Text>
            </Keyboard>
          </FooterAction>
          <Icon name="slash" size={12} color="var(--border-alpha)" />
          <FooterAction onPress={onTypeInstead}>
            <Text variant="text-smaller-md">Chat instead</Text>
            <Keyboard
              style={{ padding: "2px 4px", gap: 2, display: "inline-flex" }}
            >
              <Text variant="text-smaller-md">⇧</Text>
              <Text variant="text-smaller-md">esc</Text>
            </Keyboard>
          </FooterAction>
        </Instructions>
        <Instructions>
          <Text variant="text-smaller-md">Select </Text>
          <Keyboard>
            <Icon name="corner-down-left" size={12} />
          </Keyboard>
        </Instructions>
      </Footer>
    </PromptContainer>
  );
}

function UnavailablePrompt({
  target,
  othersPresent,
  onDismiss,
  onTypeInstead,
}: {
  target?: string;
  othersPresent: boolean;
  onDismiss: () => void;
  onTypeInstead: () => void;
}) {
  return (
    <PromptContainer>
      <PromptHeader>
        <Text variant="text-small">
          {target ? `Connect to ${target}` : "Connect a connector"}
        </Text>
      </PromptHeader>
      <UnavailableMessage>
        <UnavailableRow>
          <Icon name="lock" size={16} color="var(--text-secondary)" />
          <Text variant="text-regular" color="var(--text-primary)">
            You don’t have permission to add a connector.
          </Text>
        </UnavailableRow>
        {othersPresent && (
          <UnavailableRow>
            <Icon name="users-round" size={16} color="var(--text-secondary)" />
            <Text variant="text-small" color="var(--text-secondary)">
              A collaborator with access can connect it — skip only if no one
              will.
            </Text>
          </UnavailableRow>
        )}
      </UnavailableMessage>
      <Footer>
        <Instructions>
          <FooterAction onPress={onDismiss}>
            <Text variant="text-smaller-md">Skip</Text>
            <Keyboard style={{ padding: "2px 4px" }}>
              <Text variant="text-smaller-md">esc</Text>
            </Keyboard>
          </FooterAction>
          <Icon name="slash" size={12} color="var(--border-alpha)" />
          <FooterAction onPress={onTypeInstead}>
            <Text variant="text-smaller-md">Chat instead</Text>
            <Keyboard
              style={{ padding: "2px 4px", gap: 2, display: "inline-flex" }}
            >
              <Text variant="text-smaller-md">⇧</Text>
              <Text variant="text-smaller-md">esc</Text>
            </Keyboard>
          </FooterAction>
        </Instructions>
      </Footer>
    </PromptContainer>
  );
}

export function ConfirmPrompt({
  title,
  description,
  hint,
  confirmLabel,
  onConfirm,
  onDismiss,
  onTypeInstead,
}: {
  title: string;
  description: string;
  hint: string;
  confirmLabel: string;
  onConfirm: () => void;
  onDismiss: () => void;
  onTypeInstead: () => void;
}) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEditableTarget(e)) return;
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        onConfirm();
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) onTypeInstead();
        else onDismiss();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onConfirm, onDismiss, onTypeInstead]);

  return (
    <PromptContainer>
      <PromptHeader>
        <Text variant="text-small">{title}</Text>
      </PromptHeader>
      <UnavailableMessage>
        <UnavailableRow>
          <Icon name="lock" size={16} color="var(--text-secondary)" />
          <Text variant="text-regular" color="var(--text-primary)">
            {description}
          </Text>
        </UnavailableRow>
        <UnavailableRow>
          <Icon name="users-round" size={16} color="var(--text-secondary)" />
          <Text variant="text-small" color="var(--text-secondary)">
            {hint}
          </Text>
        </UnavailableRow>
        <ConfirmActions>
          <Button onClick={onConfirm}>{confirmLabel}</Button>
        </ConfirmActions>
      </UnavailableMessage>
      <Footer>
        <Instructions>
          <FooterAction onPress={onDismiss}>
            <Text variant="text-smaller-md">Not now</Text>
            <Keyboard style={{ padding: "2px 4px" }}>
              <Text variant="text-smaller-md">esc</Text>
            </Keyboard>
          </FooterAction>
          <Icon name="slash" size={12} color="var(--border-alpha)" />
          <FooterAction onPress={onTypeInstead}>
            <Text variant="text-smaller-md">Chat instead</Text>
            <Keyboard
              style={{ padding: "2px 4px", gap: 2, display: "inline-flex" }}
            >
              <Text variant="text-smaller-md">⇧</Text>
              <Text variant="text-smaller-md">esc</Text>
            </Keyboard>
          </FooterAction>
        </Instructions>
        <Instructions>
          <Text variant="text-smaller-md">Confirm </Text>
          <Keyboard>
            <Icon name="corner-down-left" size={12} />
          </Keyboard>
        </Instructions>
      </Footer>
    </PromptContainer>
  );
}

const ConfirmActions = styled("div")`
  display: flex;
  justify-content: flex-end;
  padding: 4px 8px 8px;
`;
